import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users, Plus, Trash2, Copy, Check, Lock, Unlock,
  AlertCircle, Eye, EyeOff, Mail, RefreshCw, X, Pencil,
} from "lucide-react";

const ADMIN_PASSWORD = (import.meta.env.VITE_ADMIN_PASSWORD as string | undefined) ?? "admin";

interface Client {
  id: string;
  name: string;
  username: string;
  password: string;
  email: string;
  notes: string;
  is_active: boolean;
  created_at: string;
}

function adminHeaders() {
  return { "Content-Type": "application/json", "x-admin-token": ADMIN_PASSWORD };
}

function generatePassword(len = 12) {
  const chars = "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$%";
  return Array.from({ length: len }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
}

function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(text);
  }
  // Fallback for HTTP (non-secure) contexts
  const el = document.createElement("textarea");
  el.value = text;
  el.style.cssText = "position:fixed;top:-9999px;left:-9999px;opacity:0";
  document.body.appendChild(el);
  el.focus();
  el.select();
  document.execCommand("copy");
  document.body.removeChild(el);
  return Promise.resolve();
}

function CopyBtn({ text, label = "" }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    copyToClipboard(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button
      onClick={copy}
      title={`Copy ${label}`}
      className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs text-white/50 hover:bg-white/10 hover:text-white transition"
    >
      {copied ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

export default function ClientAccessManager() {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ name: "", username: "", password: "", email: "", notes: "" });
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showPw, setShowPw] = useState<Record<string, boolean>>({});
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch("/api/clients", { headers: adminHeaders() });
      if (!r.ok) throw new Error(await r.text());
      setClients(await r.json());
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ name: "", username: "", password: generatePassword(), email: "", notes: "" });
    setFormError(null);
    setShowForm(true);
  };

  const openEdit = (c: Client) => {
    setEditingId(c.id);
    setForm({ name: c.name, username: c.username, password: c.password, email: c.email, notes: c.notes });
    setFormError(null);
    setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setEditingId(null); };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setSaving(true);
    try {
      const url = editingId ? `/api/clients/${editingId}` : "/api/clients";
      const method = editingId ? "PATCH" : "POST";
      const r = await fetch(url, {
        method,
        headers: adminHeaders(),
        body: JSON.stringify(form),
      });
      const data = await r.json();
      if (!r.ok) { setFormError(data.error || "Save failed"); return; }
      await load();
      closeForm();
    } catch (e: unknown) {
      setFormError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (c: Client) => {
    try {
      await fetch(`/api/clients/${c.id}`, {
        method: "PATCH",
        headers: adminHeaders(),
        body: JSON.stringify({ is_active: !c.is_active }),
      });
      await load();
    } catch {
      /* ignore */
    }
  };

  const handleDelete = async (id: string) => {
    if (deletingId !== id) { setDeletingId(id); return; }
    try {
      await fetch(`/api/clients/${id}`, { method: "DELETE", headers: adminHeaders() });
      setDeletingId(null);
      await load();
    } catch {
      /* ignore */
    }
  };

  const credentialsText = (c: Client) =>
    `Immense Estate – Client Portal\nURL: ${window.location.origin}\nUsername: ${c.username}\nPassword: ${c.password}\n\nPlease keep these credentials secure.`;

  return (
    <div className="mt-8 w-full">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 text-navy-950">
            <Users className="h-5 w-5" />
          </div>
          <div>
            <div className="text-[10px] font-bold tracking-[0.25em] text-emerald-400 uppercase">Manage</div>
            <div className="text-lg font-bold text-white">Client Access</div>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-white/70 hover:bg-white/10 transition"
          >
            <RefreshCw className="h-3 w-3" /> Refresh
          </button>
          <button
            onClick={openCreate}
            className="flex items-center gap-1.5 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600 px-4 py-1.5 text-xs font-bold text-white shadow-lg shadow-emerald-500/20 hover:brightness-110 transition"
          >
            <Plus className="h-3.5 w-3.5" /> New Client
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">
          <AlertCircle className="h-3.5 w-3.5" /> {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="text-center py-12 text-white/40 text-sm">Loading clients…</div>
      )}

      {/* Empty */}
      {!loading && clients.length === 0 && !error && (
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-10 text-center">
          <Users className="h-10 w-10 mx-auto text-white/20 mb-3" />
          <div className="text-sm text-white/40">No clients yet. Click <b>New Client</b> to create one.</div>
        </div>
      )}

      {/* Client list */}
      {!loading && clients.length > 0 && (
        <div className="space-y-3">
          {clients.map((c) => (
            <div
              key={c.id}
              className={`rounded-xl border ${c.is_active ? "border-white/[0.07]" : "border-rose-500/20"} bg-white/[0.03] p-4`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-white">{c.name}</span>
                    {!c.is_active && (
                      <span className="rounded-full border border-rose-500/40 bg-rose-500/10 px-2 py-0.5 text-[10px] font-bold text-rose-300 uppercase tracking-wider">
                        Locked
                      </span>
                    )}
                  </div>
                  {c.email && (
                    <div className="mt-0.5 text-[11px] text-white/40 flex items-center gap-1">
                      <Mail className="h-3 w-3" /> {c.email}
                    </div>
                  )}
                  {c.notes && (
                    <div className="mt-0.5 text-[11px] text-white/35 italic">{c.notes}</div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => openEdit(c)}
                    title="Edit"
                    className="rounded-lg border border-white/10 bg-white/5 p-1.5 text-white/60 hover:bg-white/10 hover:text-white transition"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => toggleActive(c)}
                    title={c.is_active ? "Lock access" : "Unlock access"}
                    className={`rounded-lg border p-1.5 transition ${c.is_active ? "border-amber-400/30 bg-amber-400/10 text-amber-300 hover:bg-amber-400/20" : "border-emerald-400/30 bg-emerald-400/10 text-emerald-300 hover:bg-emerald-400/20"}`}
                  >
                    {c.is_active ? <Lock className="h-3.5 w-3.5" /> : <Unlock className="h-3.5 w-3.5" />}
                  </button>
                  <button
                    onClick={() => handleDelete(c.id)}
                    title={deletingId === c.id ? "Click again to confirm" : "Delete"}
                    className={`rounded-lg border p-1.5 transition ${deletingId === c.id ? "border-rose-500/50 bg-rose-500/20 text-rose-300" : "border-white/10 bg-white/5 text-white/50 hover:bg-rose-500/10 hover:text-rose-300 hover:border-rose-500/30"}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>

              {/* Credentials row */}
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 rounded-lg border border-white/[0.05] bg-black/20 px-3 py-2 text-xs">
                <div className="flex items-center gap-1.5 text-white/50">
                  <span className="text-white/30">user:</span>
                  <span className="font-mono text-white/80">{c.username}</span>
                  <CopyBtn text={c.username} label="username" />
                </div>
                <div className="flex items-center gap-1.5 text-white/50">
                  <span className="text-white/30">pass:</span>
                  <span className="font-mono text-white/80">
                    {showPw[c.id] ? c.password : "••••••••"}
                  </span>
                  <button
                    onClick={() => setShowPw((p) => ({ ...p, [c.id]: !p[c.id] }))}
                    className="text-white/30 hover:text-white/60 transition"
                  >
                    {showPw[c.id] ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                  </button>
                  <CopyBtn text={c.password} label="password" />
                </div>
                <div className="ml-auto">
                  <CopyBtn text={credentialsText(c)} label="all credentials" />
                  <span className="text-white/30 ml-1">Copy for email</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit modal */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 16 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 16 }}
              className="w-full max-w-md rounded-2xl border border-white/[0.08] bg-[#0d1523] p-6 shadow-2xl"
            >
              <div className="mb-5 flex items-center justify-between">
                <h2 className="text-base font-bold text-white">{editingId ? "Edit Client" : "New Client"}</h2>
                <button onClick={closeForm} className="text-white/40 hover:text-white transition">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handleSave} className="space-y-3">
                <Field label="Full Name *" type="text" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} placeholder="e.g. Khalid Abdulla" required />
                <Field label="Username *" type="text" value={form.username} onChange={(v) => setForm((f) => ({ ...f, username: v }))} placeholder="e.g. khalid.abdulla" required />

                <div>
                  <label className="block text-[11px] font-semibold tracking-wider text-white/60 uppercase mb-1.5">Password *</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={form.password}
                      onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                      required
                      className="flex-1 rounded-lg border border-white/10 bg-navy-950/70 px-3 py-2 text-sm text-white font-mono placeholder-white/30 outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400/40"
                    />
                    <button
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, password: generatePassword() }))}
                      className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs text-white/60 hover:bg-white/10 hover:text-white transition whitespace-nowrap"
                    >
                      Generate
                    </button>
                  </div>
                </div>

                <Field label="Email" type="email" value={form.email} onChange={(v) => setForm((f) => ({ ...f, email: v }))} placeholder="client@example.com" />
                <Field label="Notes" type="text" value={form.notes} onChange={(v) => setForm((f) => ({ ...f, notes: v }))} placeholder="Internal notes (not shown to client)" />

                {formError && (
                  <div className="flex items-center gap-2 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">
                    <AlertCircle className="h-3.5 w-3.5" /> {formError}
                  </div>
                )}

                <div className="flex gap-2 pt-1">
                  <button type="button" onClick={closeForm} className="flex-1 rounded-lg border border-white/10 bg-white/5 py-2.5 text-sm text-white/70 hover:bg-white/10 transition">
                    Cancel
                  </button>
                  <button type="submit" disabled={saving} className="flex-1 rounded-lg bg-gradient-to-r from-emerald-500 to-emerald-600 py-2.5 text-sm font-bold text-white hover:brightness-110 transition disabled:opacity-60">
                    {saving ? "Saving…" : editingId ? "Save Changes" : "Create Client"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Field({
  label, type, value, onChange, placeholder, required,
}: {
  label: string; type: string; value: string; onChange: (v: string) => void; placeholder?: string; required?: boolean;
}) {
  return (
    <label className="block">
      <span className="block text-[11px] font-semibold tracking-wider text-white/60 uppercase mb-1.5">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="w-full rounded-lg border border-white/10 bg-navy-950/70 px-3.5 py-2.5 text-sm text-white placeholder-white/30 outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400/40"
      />
    </label>
  );
}
